import { Component, type ErrorInfo, type ReactNode } from 'react'

interface Props {
  readonly children: ReactNode
}

interface State {
  readonly error: Error | null
}

/**
 * A render error used to white-screen the tab with nothing to do about it.
 *
 * The game itself is safe — it lives in the Durable Object, so reloading gets
 * everything back — which makes "reload" a genuinely sufficient recovery, and
 * makes saying so worth more than a blank page.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('render failed', error, info.componentStack)
  }

  override render(): ReactNode {
    const { error } = this.state
    if (error === null) return this.props.children

    return (
      <main className="mx-auto flex min-h-full w-full max-w-md flex-col justify-center px-6 py-10 text-center">
        <h1 className="text-2xl font-bold">Something broke</h1>
        <p className="mt-2 text-sm text-ink-dim">
          The board hit a bug and stopped. Your game is safe on the server — reloading picks it up
          exactly where it was.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="mt-5 w-full rounded bg-accent px-4 py-3 font-bold text-black hover:bg-accent-strong"
        >
          Reload
        </button>
        <a
          href="/"
          className="mt-2 rounded px-4 py-2 text-sm font-semibold text-ink-dim hover:text-ink"
        >
          Back to the home page
        </a>
        <pre className="mt-6 overflow-x-auto rounded bg-panel p-3 text-left text-[11px] text-ink-dim">
          {error.message}
        </pre>
      </main>
    )
  }
}
